import { collection, getDocs, doc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Migrates old user-centric children data to the new centralized children collection.
 * Old path: users/{uid}/children/{childId}
 * New path: children/{childId}
 */
export const migrateUserChildrenToCentral = async (user) => {
    if (!user || !user.uid) return;
    
    // Check if migration has already been run for this user
    // We can use localStorage as a simple flag to avoid running it every time
    const migrationKey = `migration_v2_${user.uid}`;
    if (localStorage.getItem(migrationKey)) {
        return;
    }

    console.log("Starting migration for user:", user.uid);
    try {
        const oldChildrenRef = collection(db, `users/${user.uid}/children`);
        const snapshot = await getDocs(oldChildrenRef);
        
        if (snapshot.empty) {
            console.log("No old children found to migrate.");
            localStorage.setItem(migrationKey, 'done');
            return;
        }

        console.log(`Found ${snapshot.size} children to migrate.`);
        const batch = writeBatch(db);

        for (const childDoc of snapshot.docs) {
            const childData = childDoc.data();
            const childId = childDoc.id;
            
            // Prepare new centralized document
            const newChildRef = doc(db, 'children', childId);
            const newChildData = {
                ...childData,
                nik: childData.nik || '', // Add NIK if missing
                parents: [user.uid], // Set the creator as parent
                nakes: [],
                teachers: [],
                migratedAt: new Date()
            };
            
            // Remove ownerUid if we want, or keep it. Let's keep it for compatibility but add arrays.
            batch.set(newChildRef, newChildData, { merge: true });

            // We must also migrate subcollections (assessments, interventionPlans, privateData)
            // But Firestore batch doesn't easily do deep copies without querying them.
            // Since this is a client-side migration, we will query them one by one.
            const collectionsToCopy = ['assessments', 'interventionPlans', 'privateData'];
            
            for (const colName of collectionsToCopy) {
                const subColRef = collection(db, `users/${user.uid}/children/${childId}/${colName}`);
                const subSnap = await getDocs(subColRef);
                subSnap.docs.forEach(subDoc => {
                    const newSubRef = doc(db, `children/${childId}/${colName}`, subDoc.id);
                    batch.set(newSubRef, subDoc.data(), { merge: true });
                });
            }
            
            // We DO NOT delete the old docs here for safety. 
            // We just leave them orphaned or we could delete them once confirmed. 
            // For now, leaving them is safer.
        }

        await batch.commit();
        console.log("Migration successful!");
        localStorage.setItem(migrationKey, 'done');
    } catch (error) {
        console.error("Migration failed:", error);
    }
};
